import {
  LoteTempNuevo,
  registroFincaTempGrpcService,
} from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import {
  CambioEstructuralLotes,
  fx_cajaDePoligono,
  fx_unirCajas,
  LoteSplitEntry,
  LoteSplitState,
} from "./LoteSplitState";

/** `[minLng, minLat, maxLng, maxLat]` de lo que cambió con una operación. */
type ZonaCambiada = [number, number, number, number] | null;

/** Refleja en el registro temporal del servidor lo que el usuario hace con
 *  los lotes del paso 4, y NADA más: cada "Dividir" y cada "Unir"
 *  (`ReemplazarLotes`, por id) y cada nombre que el usuario escribe
 *  (`ActualizarLotes`). Abrir el paso, pulsar "Volver" o "Continuar" no
 *  escriben nada por su cuenta.
 *
 *  **Una cola, en orden.** Las operaciones se mandan de a una y en el orden
 *  en que ocurrieron: el usuario puede cortar una pieza recién creada antes
 *  de que su alta vuelva del servidor, y para entonces la operación
 *  siguiente ya necesita el id de esa pieza. Cada operación lee los ids
 *  (`entry.tempId`) recién al ejecutarse, cuando las anteriores ya los
 *  asignaron.
 *
 *  **Si algo falla, se deja de mandar.** La pantalla y la base ya no
 *  coinciden, y seguir mandando operaciones sobre esa base podría
 *  empeorarlo. Se avisa (`onError`) y quien usa esta clase recarga los
 *  lotes desde la base (`RegistroFincaPaso4Render`): manda la base, nunca
 *  la memoria. */
export class LotesTempSync {
  private _cola: Promise<void> = Promise.resolve();
  private _fallo = false;
  /** Último nombre que el servidor tiene de cada lote, para mandar solo los
   *  que el usuario cambió. */
  private readonly _nombresGuardados = new WeakMap<LoteSplitEntry, string>();

  /** @param _onPersistido Una operación quedó guardada. Es lo que le dice al
   *   mapa que ya puede repedirle los tiles a GeoServer: los lotes los dibuja
   *   el WMS desde la base, así que repintar antes de esto mostraría el estado
   *   anterior — la geometría vieja tras un corte, o el nombre viejo en la
   *   etiqueta tras un renombre. Recibe la caja de los lotes que tocó la
   *   operación, para repintar sólo esa zona (2026-10-27); `null` = repintar
   *   todo, como antes. */
  constructor(
    private readonly _registroId: string,
    private readonly _state: LoteSplitState,
    private readonly _onError: (error: unknown) => void,
    private readonly _onPersistido: ((zona: ZonaCambiada) => void) | null = null,
  ) {}

  /** Empieza a escuchar los cambios del estado. */
  conectar(): void {
    this._state.addOnCambioEstructuralListener(this._alCambioEstructural);
    this._state.addOnChangeListener(this._sincronizarNombres);
  }

  /** Registra los lotes tal como se leyeron de la base (sus nombres son los
   *  guardados). Se llama al montar y cada vez que se recargan. */
  adoptar(): void {
    this._fallo = false;
    this._state.getAll().forEach((entry) => {
      this._nombresGuardados.set(entry, entry.nombreLote);
    });
  }

  /** Espera a que termine todo lo encolado. Rechaza si alguna operación
   *  falló: la base no refleja lo que hay en pantalla. */
  async confirmarAsync(): Promise<void> {
    await this._cola;
    if (this._fallo) {
      throw new Error(
        "Uno de los cambios de lotes no se pudo guardar. Se recargaron los lotes guardados; revise y vuelva a intentar.",
      );
    }
  }

  /** Un borrado (hoy el único cambio estructural que arma el cliente): se
   *  manda a `ReemplazarLotes`. La zona a repintar son los lotes que salieron,
   *  los que entraron y los vecinos que cambiaron. */
  private _alCambioEstructural = (cambio: CambioEstructuralLotes): void => {
    const zona = [...cambio.eliminados, ...cambio.agregados, ...cambio.vecinos]
      .reduce<ZonaCambiada>(
        (total, entry) => fx_unirCajas(total, fx_cajaDePoligono(entry.polygon)),
        null,
      );
    this._encolar(async () => {
      const vigentes = new Set(this._state.getAll());
      const ids = await registroFincaTempGrpcService.reemplazarLotesAsync(
        this._registroId,
        {
          eliminarIds: cambio.eliminados
            .map((entry) => entry.tempId)
            .filter((id): id is string => !!id),
          agregar: cambio.agregados.map(aLoteNuevo),
          // Un vecino que ya no está (lo cortó o unió una operación
          // posterior) no se toca: esa operación manda su propio cambio.
          actualizarGeometria: cambio.vecinos
            .filter((entry) => entry.tempId && vigentes.has(entry))
            .map((entry) => ({ id: entry.tempId!, polygon: entry.polygon })),
        },
      );
      cambio.agregados.forEach((entry, index) => {
        entry.tempId = ids[index] ?? null;
        this._nombresGuardados.set(entry, entry.nombreLote);
      });
    }, zona);
  };

  /** Manda los nombres que el usuario cambió. Los lotes todavía sin id se
   *  saltean: su alta, al ejecutarse, lleva el nombre que tengan. */
  private _sincronizarNombres = (): void => {
    const cambiados = this._state
      .getAll()
      .filter(
        (entry) =>
          entry.tempId &&
          this._nombresGuardados.has(entry) &&
          this._nombresGuardados.get(entry) !== entry.nombreLote,
      );
    if (cambiados.length === 0) return;

    const actualizaciones = cambiados.map((entry) => ({
      id: entry.tempId!,
      nombreLote: entry.nombreLote,
    }));
    cambiados.forEach((entry) =>
      this._nombresGuardados.set(entry, entry.nombreLote),
    );
    // Un nombre nuevo sólo cambia la etiqueta de ese lote.
    const zona = cambiados.reduce<ZonaCambiada>(
      (total, entry) => fx_unirCajas(total, fx_cajaDePoligono(entry.polygon)),
      null,
    );

    this._encolar(
      () =>
        registroFincaTempGrpcService.actualizarLotesAsync(
          this._registroId,
          actualizaciones,
        ),
      zona,
    );
  };

  /** Encola una operación y, si se guardó, avisa con la zona que tocó
   *  (`_onPersistido`). */
  private _encolar(operacion: () => Promise<void>, zona: ZonaCambiada): void {
    this._cola = this._cola.then(async () => {
      // Después de una falla no se manda nada más hasta recargar.
      if (this._fallo) return;
      try {
        await operacion();
        this._onPersistido?.(zona);
      } catch (error) {
        this._fallo = true;
        this._onError(error);
      }
    });
  }
}

function aLoteNuevo(entry: LoteSplitEntry): LoteTempNuevo {
  return {
    nombreLote: entry.nombreLote,
    areaGeograficaPolygon: entry.polygon,
    loteOrigenId: entry.loteOrigenId,
    isAreaProductiva: entry.asignacion?.isAreaProductiva ?? null,
    cultivoId: entry.asignacion?.cultivoId ?? null,
    coberturaId: entry.asignacion?.coberturaId ?? null,
  };
}
